import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import apiClient from "utils/apiClient";

export default function useTaskCategories() {
  const authToken = sessionStorage.getItem("authToken");

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${authToken}`,
      "Content-Type": "application/json",
    }),
    [authToken]
  );
  const [categories, setCategories] = useState(null);
  const [loading, setLoading] = useState(false);

  // handle axios errors
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      message =
        err.response.data?.message ||
        `Failed to ${action}: ${err.response.statusText}`;
    } else if (err.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else {
      message = err.message || "Something went wrong.";
    }
    toast.error(message);
    console.error(`${action} error:`, err);
  };

  // fetch all task categories
  const fetchAllCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(
        "/olitask-service/api/v1/task-categories/tree",
        { headers }
      );

      setCategories(res.data);
    } catch (error) {
      handleAxiosErrors(error, "fetch all task categories");
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  // add root node
  const addRootNode = useCallback(
    async (payload) => {
      // setLoading(true);
      try {
        const res = await apiClient.post(
          "/olitask-service/api/v1/task-categories",
          payload,
          { headers }
        );

        toast.success("Root Category added successfully");
        return res.data;
      } catch (error) {
        handleAxiosErrors(error, "add root node");
      }
    },
    [authToken]
  );

  // add subcategory node
  const addSubCategory = useCallback(
    async (payload) => {
      // setLoading(true);
      try {
        const res = await apiClient.post(
          "/olitask-service/api/v1/task-categories",
          payload,
          { headers }
        );

        toast.success("Category added successfully");
        return res.data;
      } catch (error) {
        handleAxiosErrors(error, "add subcategory node");
      }
    },
    [authToken]
  );

  // update subcategory node
  const updateCategory = useCallback(
    async (id, payload) => {
      try {
        const res = await apiClient.put(
          "/olitask-service/api/v1/task-categories/" + id,
          payload,
          { headers }
        );

        toast.success("Category update successfully");
        return res.data;
      } catch (error) {
        handleAxiosErrors(error, "update category node");
      }
    },
    [authToken]
  );

  // delete category node
  const deleteCategory = useCallback(
    async (id) => {
      try {
        await apiClient.delete(
          "/olitask-service/api/v1/task-categories/" + id,
          { headers }
        );

        toast.success("Category deleted successfully");
      } catch (error) {
        handleAxiosErrors(error, "delete node");
      }
    },
    [authToken]
  );

  useEffect(() => {
    if (authToken) fetchAllCategories();
  }, [authToken, fetchAllCategories]);

  return {
    categories,
    setCategories,
    addRootNode,
    addSubCategory,
    updateCategory,
    deleteCategory,
    loading,
  };
}
